import { FC, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Upload } from "@mui/icons-material";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MainLayoutContainer } from "components/Layout";
import { EntitySelector } from "modals/EntitySelector";
import { getFilteredList } from "utils/Array";
import {
  useAddGraduationClassMutation,
  useAddGraduationClassStudentMutation,
  useAddGraduationClassTagMutation,
  useDeleteGraduationClassStudentMutation,
  useDeleteGraduationClassTagByTagAndGraduationClassMutation,
  useGetAcademicYearsByOrgQuery,
  useGetGraduationClassesByIdLazyQuery,
  useGetUserByRoleQuery,
  useUpdateGraduationClassMutation,
} from "graphql/__generated__/hooks";
import { ErrorMessage, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { AcademicYearSelectionType } from "types/academicYear";
import { GraduationClass } from "types/graduationClass";
import { User } from "types/user";
import { getAcademicYearDates } from "utils/Datetime";
import { useProgramOrganization } from "hooks/useProgramOrganization";
import { Tag } from "types/tag";
import { TagManager } from "components/TagManager";
import { ListWithEntitySelector } from "components/ListWithEntitySelector";
import { useAvailableStudents } from "hooks/useAvailableStudents";
import { useNotifications } from "hooks/useNotifications";
import { Messages, NotificationSeverity } from "helpers/constants";
import { useClinicText } from "hooks/useClinicText";

type GraduationClassCreateOrEditProps = { edit?: boolean };

export const GraduationClassCreateOrEdit: FC<
  GraduationClassCreateOrEditProps
> = ({ edit = false }) => {
  const { graduationClassId, organizationId } = useParams();
  const { program } = useProgramOrganization();
  const { textGraduationClass } = useClinicText();
  const navigate = useNavigate();
  const [originalStudents, setOriginalStudents] = useState<User[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [openEntityAcademicYearSelector, setOpenEntityAcademicYearSelector] =
    useState<boolean>(false);
  const [selectedAcademicYear, setSelectedAcademicYear] =
    useState<AcademicYearSelectionType | null>(null);
  const [students, setStudents] = useState<User[]>([]);
  const [openEntityStudentSelector, setOpenEntityStudentSelector] =
    useState(false);
  const [loading, setLoading] = useState(false);

  const [addGraduationClass] = useAddGraduationClassMutation();
  const [updateGraduationClass] = useUpdateGraduationClassMutation();
  const [addGraduationClassStudent] = useAddGraduationClassStudentMutation();
  const [deleteGraduationClassStudent] =
    useDeleteGraduationClassStudentMutation();
  const [getGraduationClassById, { data: dataGraduationClass }] =
    useGetGraduationClassesByIdLazyQuery();
  const [addGraduationClassTag] = useAddGraduationClassTagMutation();
  const [deleteGraduationClassTag] =
    useDeleteGraduationClassTagByTagAndGraduationClassMutation();
  const { notify } = useNotifications();

  const { data: dataStudents } = useGetUserByRoleQuery({
    variables: { role: "student" },
  });
  const { availableStudents } = useAvailableStudents();

  const backRoute = `/organization/${organizationId}/graduation-class`;
  const { start_date } = getAcademicYearDates();

  const { data: academicYearData } = useGetAcademicYearsByOrgQuery({
    variables: { orgId: organizationId },
  });

  const academicYears = useMemo(() => {
    return (
      academicYearData?.program
        ?.map((program) => program.academic_years)
        .flat() || []
    );
  }, [academicYearData]);

  const initialValues = {
    name: "",
    start_date,
  };

  useEffect(() => {
    if (edit) {
      getGraduationClassById({
        variables: { graduation_class_id: graduationClassId ?? "" },
      });
    }
  }, [edit]);

  useEffect(() => {
    if (dataGraduationClass) {
      formik.setValues({
        name: dataGraduationClass.graduation_class_by_pk?.name ?? "",
        start_date:
          dataGraduationClass.graduation_class_by_pk?.start_date ?? "",
      });
      const currentStudents =
        dataGraduationClass.graduation_class_by_pk?.students.map(
          (student) => student.student,
        ) as User[];

      const currentTags = dataGraduationClass.graduation_class_by_pk?.tags.map(
        (tag: any) => tag.tag,
      ) as Tag[];
      setStudents(currentStudents);
      setOriginalStudents(currentStudents);
      setTags(currentTags);
    }
  }, [dataGraduationClass]);

  useEffect(() => {
    if (
      dataGraduationClass?.graduation_class_by_pk?.current_academic_year_id &&
      academicYears?.length > 0
    ) {
      const filtered_academicYear = academicYears.filter(
        (ay) =>
          ay.academic_year_id ===
          dataGraduationClass?.graduation_class_by_pk?.current_academic_year_id,
      );
      if (filtered_academicYear.length > 0) {
        setSelectedAcademicYear(
          filtered_academicYear[0] as AcademicYearSelectionType,
        );
      }
    }
  }, [dataGraduationClass, academicYears]);

  const validationSchema = () =>
    Yup.object().shape({
      name: Yup.string().required("This field is required"),
    });

  const handleCreateGraduationClassStudents = async (
    graduationClassId: string,
  ) => {
    if (students.length === 0) return;

    students.forEach(async (student) => {
      await addGraduationClassStudent({
        variables: {
          graduation_class_id: graduationClassId,
          student_id: student.user_id,
        },
      });
    });
  };

  const handleEditGraduationClassStudents = async (
    graduationClassId: string,
  ) => {
    const newStudents = students.filter(
      (student) =>
        !originalStudents.some((orig) => orig.user_id === student.user_id),
    );
    const removedStudents = originalStudents.filter(
      (orig) => !students.some((student) => student.user_id === orig.user_id),
    );
    const removedGraduationClassStudentId =
      dataGraduationClass?.graduation_class_by_pk?.students
        ?.filter((graduationClassStudent) =>
          removedStudents.some(
            (student) =>
              student.user_id === graduationClassStudent?.student?.user_id,
          ),
        )
        .map(
          (graduationClassStudent) =>
            graduationClassStudent.graduation_class_student_id,
        );
    for (const newStudent of newStudents) {
      await addGraduationClassStudent({
        variables: {
          graduation_class_id: graduationClassId,
          student_id: newStudent.user_id,
        },
      });
    }
    if (!removedGraduationClassStudentId) return;
    for (const graduationClassStudentId of removedGraduationClassStudentId) {
      await deleteGraduationClassStudent({
        variables: {
          id: graduationClassStudentId,
        },
      });
    }
  };

  /* Create graduation class */
  const handleCreateGraduationClass = async (reqValues: GraduationClass) => {
    setLoading(true);

    const result = await addGraduationClass({
      variables: reqValues,
    });

    const graduationClassId =
      result.data?.insert_graduation_class?.returning[0]?.graduation_class_id;
    if (graduationClassId) {
      await handleCreateGraduationClassStudents(graduationClassId).then();
    }
    setLoading(false);
    navigate(backRoute);
    notify({
      message: Messages.successGraduationClassCreated,
      severity: NotificationSeverity.success,
    });
  };

  /* Edit program */
  const handleEditGraduationClass = async (reqValues: GraduationClass) => {
    setLoading(true);

    await updateGraduationClass({
      variables: {
        id: graduationClassId ?? "",
        graduationClass: reqValues,
      },
    });
    if (graduationClassId) {
      await handleEditGraduationClassStudents(graduationClassId).then();
    }
    setLoading(false);
    navigate(backRoute);
    notify({
      message: Messages.successGraduationClassUpdated,
      severity: NotificationSeverity.success,
    });
  };

  const handleAddTag = async (tag: Tag) => {
    await addGraduationClassTag({
      variables: { tagId: tag.tag_id, graduationClassId: graduationClassId },
    });
    setTags([...tags, tag]);
  };

  const handleDeleteTag = async (tagToRemove: Tag) => {
    await deleteGraduationClassTag({
      variables: {
        tagId: tagToRemove.tag_id,
        graduationClassId: graduationClassId,
      },
    });
    const newTags = tags.filter((tag) => tag.tag_id !== tagToRemove.tag_id);
    setTags(newTags);
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const reqValues = {
        name: values.name,
        start_date: values.start_date,
        program_id: program?.program_id || "",
        current_academic_year_id: selectedAcademicYear?.academic_year_id ?? "",
      };
      if (edit) {
        handleEditGraduationClass(reqValues as GraduationClass).then();
      } else {
        handleCreateGraduationClass(reqValues as GraduationClass).then();
      }
    },
  });

  return (
    <MainLayoutContainer
      title={`${edit ? "Edit" : "Create"} ${textGraduationClass}`}
    >
      <Box sx={{ maxWidth: 860 }}>
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <Box mb={2}>
              <TextField
                variant="standard"
                onChange={formik.handleChange}
                fullWidth
                name="name"
                label="Name"
                type="text"
                id="name"
                value={formik?.values?.name}
              />
              <ErrorMessage name="name" />
            </Box>

            <Box mb={2}>
              <TextField
                variant="standard"
                onChange={formik.handleChange}
                fullWidth
                name="start_date"
                label="Start Date"
                type="date"
                id="start_date"
                value={formik?.values?.start_date}
              />
              <ErrorMessage name="start_date" />
            </Box>

            <Box mt={2}>
              <Typography variant="h6">AcademicYear</Typography>
              <Box>
                <Box my={2}>
                  {!selectedAcademicYear && (
                    <Typography variant="body2">No Program Selected</Typography>
                  )}
                  {selectedAcademicYear && (
                    <>
                      <Typography variant="body1" fontWeight={"bold"}>
                        {selectedAcademicYear.name}
                      </Typography>
                      <Typography variant="body2">
                        {selectedAcademicYear.start_date} -{" "}
                        {selectedAcademicYear.end_date}
                      </Typography>
                    </>
                  )}
                </Box>
                <Button
                  variant="outlined"
                  onClick={() => setOpenEntityAcademicYearSelector(true)}
                >
                  Select Program
                </Button>

                <EntitySelector
                  open={openEntityAcademicYearSelector}
                  onClose={() => setOpenEntityAcademicYearSelector(false)}
                  title={"Select Program"}
                  listEntities={academicYears}
                  filterCriteria={(search: string, data: any) =>
                    data.name.toLowerCase().includes(search.toLowerCase())
                  }
                  onSelect={(selected: any) => {
                    setSelectedAcademicYear(
                      selected as AcademicYearSelectionType,
                    );
                  }}
                  displayField="name"
                  keyField="academic_year_id"
                />
              </Box>
            </Box>

            <Divider sx={{ mt: 2, mb: 2 }} />

            {edit && (
              <Box mt={2}>
                <Box mb={1}>
                  <Typography variant="h6">Tags</Typography>
                </Box>
                <TagManager
                  tags={tags}
                  onAdd={handleAddTag}
                  onDelete={handleDeleteTag}
                  isEditing={edit}
                />
              </Box>
            )}

            <Divider sx={{ mt: 2, mb: 2 }} />

            <ListWithEntitySelector
              label="Learner"
              entityList={availableStudents}
              elements={students}
              onChangeElements={setStudents}
              entityName="Learner"
              displayField="name"
              displayFieldSecondary="email"
              idField="user_id"
            />

            {edit && (
              <Box mt={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  component={Link}
                  to={`/organization/${organizationId}/graduation-class/${graduationClassId}/upload-csv-users`}
                  startIcon={<Upload />}
                >
                  Add Students from CSV file
                </Button>
              </Box>
            )}

            {dataStudents && (
              <EntitySelector
                open={openEntityStudentSelector}
                onClose={() => setOpenEntityStudentSelector(false)}
                title={"Select Students"}
                listEntities={getFilteredList(
                  dataStudents.user,
                  students,
                  "user_id",
                )}
                filterCriteria={(search: string, data: any) =>
                  data.name.toLowerCase().includes(search.toLowerCase())
                }
                onSelect={(selected: any) => {
                  setStudents([...students, selected]);
                }}
                displayField="name"
                keyField="user_id"
              />
            )}

            <Box mt={2}>
              <Stack direction="row-reverse" spacing={2}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={loading}
                >
                  Save Changes
                </Button>
                <Button variant="outlined" onClick={() => navigate(backRoute)}>
                  Cancel
                </Button>
              </Stack>
            </Box>
          </form>
        </FormikProvider>
      </Box>
    </MainLayoutContainer>
  );
};
